import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface TenantContextType {
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  isDemoTenant: boolean;
  // Whether a demo tenant should still fall back to the built-in mock
  // dataset (src/data/mockData.ts) when its live tables are empty.
  // Defaults to true; the superadmin demo-data tool sets it to false on
  // Flush (and back to true on Populate/Re-seed) so a flushed demo tenant
  // shows a genuinely empty app for real-data demonstrations.
  demoMockDataEnabled: boolean;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { slug: slugFromUrl } = useParams<{ slug: string }>();
  const { tenantId: jwtTenantId, tenantSlug } = useAuth();
  const navigate = useNavigate();
  // Resolved from the tenants table by slug in the effect below (same
  // query that already backs tenantName/isDemoTenant) - authoritative,
  // and doesn't depend on the JWT's app_metadata.tenant_id claim being
  // current. Consumers that need a tenant UUID (e.g. useTenantMembers'
  // get_tenant_members RPC) should get a real id even when the JWT
  // claim is stale or hasn't been refreshed onto this session yet;
  // falls back to the JWT claim only until the slug lookup resolves.
  const [resolvedTenantId, setResolvedTenantId] = useState<string | null>(null);
  const tenantId = resolvedTenantId ?? jwtTenantId;
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [isDemoTenant, setIsDemoTenant] = useState(false);
  const [demoMockDataEnabled, setDemoMockDataEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Use JWT claim when present, otherwise trust the URL slug (already validated
  // by TenantLayout's DB membership check before this provider mounts).
  const effectiveSlug = tenantSlug ?? slugFromUrl ?? null;

  useEffect(() => {
    // Redirect if JWT slug disagrees with URL slug (copy-pasted / manual edit).
    if (tenantSlug && slugFromUrl && slugFromUrl !== tenantSlug) {
      navigate(`/${tenantSlug}/app`, { replace: true });
      return;
    }

    if (!effectiveSlug) {
      setIsLoading(false);
      return;
    }

    const fetchTenantName = async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, name, is_demo')
        .eq('slug', effectiveSlug)
        .single();

      // Resolve the mock-data flag BEFORE any setState below: setting
      // isDemoTenant first and awaiting afterwards would let React render
      // once with the default (enabled) flag, flashing — and in
      // state-initialising consumers like useEmails, latching — the bundled
      // mock dataset on a demo tenant that was actually flushed.
      // Only demo tenants ever consult this flag, so skip the lookup for
      // real tenants.
      let mockEnabled = true;
      if (data?.id && data?.is_demo) {
        const { data: setting } = await supabase
          .from('tenant_settings')
          .select('setting_value')
          .eq('tenant_id', data.id)
          .eq('setting_key', 'demo_mock_data_enabled')
          .maybeSingle();
        // Absent row = default enabled (never-flushed demo tenant).
        const raw = setting?.setting_value as { enabled?: boolean } | null;
        mockEnabled = raw?.enabled ?? true;
      }

      setTenantName(data?.name ?? null);
      setIsDemoTenant(data?.is_demo ?? false);
      setDemoMockDataEnabled(mockEnabled);
      setResolvedTenantId(data?.id ?? null);
      setIsLoading(false);
    };

    fetchTenantName();
  }, [effectiveSlug, tenantSlug, slugFromUrl, navigate]);

  return (
    <TenantContext.Provider value={{ tenantId, tenantSlug: effectiveSlug, tenantName, isDemoTenant, demoMockDataEnabled, isLoading }}>
      {children}
    </TenantContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
